// Firestore Database Service — persistent data layer
// Handles all Firestore CRUD operations
import { db, storage, isFirebaseReady, doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, orderBy, addDoc, writeBatch, ref, uploadBytes, getDownloadURL } from '../firebase.js';

// ============ USERS ============

export async function saveUser(user) {
    if (!isFirebaseReady()) return;
    try {
        await setDoc(doc(db, 'users', user.id), {
            name: user.name,
            pictureUrl: user.pictureUrl || '',
            role: user.role || 'customer',
            phone: user.phone || '',
            createdAt: user.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }, { merge: true });
    } catch (err) {
        console.error('saveUser error:', err);
    }
}

export async function getUser(userId) {
    if (!isFirebaseReady()) return null;
    try {
        const snap = await getDoc(doc(db, 'users', userId));
        return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (err) {
        console.error('getUser error:', err);
        return null;
    }
}

export async function updateUserRoleDB(userId, role) {
    if (!isFirebaseReady()) return;
    try {
        await updateDoc(doc(db, 'users', userId), { role, updatedAt: new Date().toISOString() });
    } catch (err) {
        console.error('updateUserRole error:', err);
    }
}

export async function updateUserProfileDB(userId, data) {
    if (!isFirebaseReady()) return;
    try {
        await setDoc(doc(db, 'users', userId), {
            ...data,
            updatedAt: new Date().toISOString()
        }, { merge: true });
    } catch (err) {
        console.error('updateUserProfile error:', err);
    }
}

export async function getAllUsers() {
    if (!isFirebaseReady()) return [];
    try {
        const snap = await getDocs(collection(db, 'users'));
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
        console.error('getAllUsers error:', err);
        return [];
    }
}

export async function getAdminUsersDB() {
    if (!isFirebaseReady()) return [];
    try {
        const q = query(collection(db, 'users'), where('role', 'in', ['fortune_teller', 'admin']));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
        console.error('getAdminUsers error:', err);
        return [];
    }
}

// ============ FILE STORAGE ============

export async function uploadFileDB(file, pathPrefix = 'uploads') {
    if (!isFirebaseReady() || !storage || !file) return null;
    try {
        const ext = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
        const storageRef = ref(storage, `${pathPrefix}/${fileName}`);
        const snapshot = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(snapshot.ref);
        return url;
    } catch (err) {
        console.error('uploadFileDB error:', err);
        return null;
    }
}

// ============ BOOKINGS ============

export async function createBooking(booking) {
    if (!isFirebaseReady()) return null;
    try {
        const docRef = await addDoc(collection(db, 'bookings'), {
            ...booking,
            status: 'pending',
            createdAt: new Date().toISOString()
        });
        return { id: docRef.id, ...booking, status: 'pending' };
    } catch (err) {
        console.error('createBooking error:', err);
        return null;
    }
}

export async function getBookingDB(bookingId) {
    if (!isFirebaseReady()) return null;
    try {
        const snap = await getDoc(doc(db, 'bookings', bookingId));
        return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (err) {
        console.error('getBookingDB error:', err);
        return null;
    }
}

export async function updateBookingDB(bookingId, updates) {
    if (!isFirebaseReady()) return;
    try {
        await updateDoc(doc(db, 'bookings', bookingId), {
            ...updates,
            updatedAt: new Date().toISOString()
        });
    } catch (err) {
        console.error('updateBooking error:', err);
    }
}

export async function getBookingsByUser(userId) {
    if (!isFirebaseReady()) return [];
    try {
        const q = query(
            collection(db, 'bookings'),
            where('user_id', '==', userId)
        );
        const snap = await getDocs(q);
        const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Sort by createdAt desc in memory to bypass composite index requirement
        return results.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
    } catch (err) {
        console.error('getBookingsByUser error:', err);
        return [];
    }
}

export async function getAllBookings() {
    if (!isFirebaseReady()) return [];
    try {
        const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
        console.error('getAllBookings error:', err);
        return [];
    }
}

// ============ ADMIN SETTINGS ============

export async function getAdminSettingsDB() {
    if (!isFirebaseReady()) return null;
    try {
        const snap = await getDoc(doc(db, 'settings', 'admin'));
        return snap.exists() ? snap.data() : null;
    } catch (err) {
        console.error('getAdminSettings error:', err);
        return null;
    }
}

export async function saveAdminSettingsDB(settings) {
    if (!isFirebaseReady()) return;
    try {
        await setDoc(doc(db, 'settings', 'admin'), {
            ...settings,
            updatedAt: new Date().toISOString()
        }, { merge: true });
    } catch (err) {
        console.error('saveAdminSettings error:', err);
    }
}

// ============ FORTUNE TELLERS ============

export async function saveFortuneTeller(ft) {
    if (!isFirebaseReady()) return null;
    try {
        if (ft.id) {
            await setDoc(doc(db, 'fortune_tellers', ft.id), {
                ...ft,
                updatedAt: new Date().toISOString()
            }, { merge: true });
            return ft;
        } else {
            const docRef = await addDoc(collection(db, 'fortune_tellers'), {
                ...ft,
                available: true,
                rating: 5.0,
                reviewCount: 0,
                createdAt: new Date().toISOString()
            });
            return { id: docRef.id, ...ft };
        }
    } catch (err) {
        console.error('saveFortuneTeller error:', err);
        return null;
    }
}

export async function getAllFortuneTellers() {
    if (!isFirebaseReady()) return [];
    try {
        const snap = await getDocs(collection(db, 'fortune_tellers'));
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
        console.error('getAllFortuneTellers error:', err);
        return [];
    }
}

export async function getFortuneTellerDB(id) {
    if (!isFirebaseReady()) return null;
    try {
        const snap = await getDoc(doc(db, 'fortune_tellers', id));
        return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (err) {
        console.error('getFortuneTeller error:', err);
        return null;
    }
}

export async function deleteFortuneTellerDB(id) {
    if (!isFirebaseReady()) return;
    try {
        const { deleteDoc: delDoc } = await import('../firebase.js');
        await delDoc(doc(db, 'fortune_tellers', id));
    } catch (err) {
        console.error('deleteFortuneTeller error:', err);
    }
}

// ============ SCHEDULES ============

export async function saveSchedule(ftId, dateStr, slots) {
    if (!isFirebaseReady()) return;
    const scheduleId = `${ftId}_${dateStr}`;
    try {
        await setDoc(doc(db, 'schedules', scheduleId), {
            fortune_teller_id: ftId,
            date: dateStr,
            slots: slots,
            booked_slots: [],
            active: true,
            updatedAt: new Date().toISOString()
        }, { merge: true });
    } catch (err) {
        console.error('saveSchedule error:', err);
    }
}

export async function updateScheduleSlotsDB(scheduleId, slots) {
    if (!isFirebaseReady()) return;
    try {
        await updateDoc(doc(db, 'schedules', scheduleId), { 
            slots: slots,
            updatedAt: new Date().toISOString()
        });
    } catch (err) {
        console.error('updateScheduleSlotsDB error:', err);
    }
}

export async function saveSchedulesBatch(ftId, datesStrArray, slots) {
    if (!isFirebaseReady() || !datesStrArray || datesStrArray.length === 0) return;
    try {
        const batch = writeBatch(db);
        const now = new Date().toISOString();
        
        datesStrArray.forEach(dateStr => {
            const scheduleId = `${ftId}_${dateStr}`;
            const scheduleRef = doc(db, 'schedules', scheduleId);
            batch.set(scheduleRef, {
                fortune_teller_id: ftId,
                date: dateStr,
                slots: slots,
                booked_slots: [],
                active: true,
                updatedAt: now
            }, { merge: true });
        });
        
        await batch.commit();
    } catch (err) {
        console.error('saveSchedulesBatch error:', err);
    }
}

export async function getSchedulesByFT(ftId) {
    if (!isFirebaseReady()) return [];
    try {
        const q = query(
            collection(db, 'schedules'),
            where('fortune_teller_id', '==', ftId),
            where('active', '==', true)
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
        console.error('getSchedulesByFT error:', err);
        return [];
    }
}

// For admin: fetch ALL schedules including inactive ones
export async function getAllSchedulesByFT(ftId) {
    if (!isFirebaseReady()) return [];
    try {
        const q = query(
            collection(db, 'schedules'),
            where('fortune_teller_id', '==', ftId)
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
        console.error('getAllSchedulesByFT error:', err);
        return [];
    }
}

export async function getAvailableDatesDB(ftId) {
    const schedules = await getSchedulesByFT(ftId);
    const today = new Date().toISOString().split('T')[0];
    return schedules
        .filter(s => s.date >= today)
        .filter(s => s.slots.length > (s.booked_slots || []).length)
        .map(s => s.date)
        .sort();
}

export async function getAvailableSlotsDB(ftId, dateStr) {
    if (!isFirebaseReady()) return [];
    const scheduleId = `${ftId}_${dateStr}`;
    try {
        const snap = await getDoc(doc(db, 'schedules', scheduleId));
        if (!snap.exists()) return [];
        const data = snap.data();
        if (data.active === false) return [];
        return (data.slots || []).filter(s => !(data.booked_slots || []).includes(s));
    } catch (err) {
        console.error('getAvailableSlots error:', err);
        return [];
    }
}

export async function bookSlotDB(ftId, dateStr, time) {
    if (!isFirebaseReady()) return;
    const scheduleId = `${ftId}_${dateStr}`;
    try {
        const snap = await getDoc(doc(db, 'schedules', scheduleId));
        if (snap.exists()) {
            const data = snap.data();
            const booked = data.booked_slots || [];
            if (!booked.includes(time)) {
                booked.push(time);
                await updateDoc(doc(db, 'schedules', scheduleId), { booked_slots: booked });
            }
        }
    } catch (err) {
        console.error('bookSlot error:', err);
    }
}

export async function toggleScheduleActive(scheduleId, active) {
    if (!isFirebaseReady()) return;
    try {
        await updateDoc(doc(db, 'schedules', scheduleId), { active });
    } catch (err) {
        console.error('toggleSchedule error:', err);
    }
}

// ============ STORAGE ============
export async function uploadAstrologyPhoto(file, bookingId) {
    if (!isFirebaseReady() || !storage) return null;
    try {
        const ext = file.name.split('.').pop() || 'jpg';
        const storageRef = ref(storage, `astrology_photos/photo_${bookingId}_${Date.now()}.${ext}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
    } catch (err) {
        console.error('uploadAstrologyPhoto error:', err);
        throw err;
    }
}

